// @flow
import * as React from 'react'
import memoize from 'mz-utils/memoize'
import importWithRetry from 'mz-utils/importWithRetry'
import { FormattedMessage } from 'mz-intl'
import getGeoCountryCode from 'mz-sdk/services/user/getGeoCountryCode'
import SelectField from '../SelectField'
import EmptySearchResults from '../SelectField/EmptySearchResults'
import messages from './messages.intl'
import FlagLabel from './FlagLabel'

// Types
import type { Props as SelectFieldProps } from '../SelectField'

export type Props = SelectFieldProps & {
  detectCountry?: boolean,
}
export type Value = {
  code: string,
  name: string,
  callingCode: string,
  flagCountryCode?: string,
}

// Utils
export const getCountries = memoize((): Promise<Array<Value>> =>
  importWithRetry(() =>
    import(/* webpackChunkName: "countries" */ './countries')
  ).then((module) => {
    const countries = module.default
    return Object.keys(countries).map((x) => ({
      code: x,
      ...(countries[x]: any),
    }))
  })
)

export const getCountryByCode = (countryCode: string): Promise<?Value> =>
  new Promise((resolve) => {
    if (!countryCode) {
      resolve(null)
    }
    getCountries()
      .then((countries) => {
        const countryValue = countries.find(
          (x) => x.code === countryCode.toLowerCase()
        )
        if (countryValue) {
          resolve(countryValue)
        } else {
          resolve(null)
        }
      })
      .catch(() => resolve(null))
  })

/**
 * A field to select country object. It uses `countries.js` file
 * as a source of all countries and load it asynchronously to be
 * able to split the list to a separate chunk, because countries
 * list is not something we need to show everywhere.
 */
class CountryField extends React.Component<Props> {
  static defaultProps = SelectField.defaultProps

  UNSAFE_componentWillMount() {
    const {
      input: { value, onChange },
      detectCountry,
    } = this.props

    const countryCodeProvided = value && value.code

    if (!countryCodeProvided && !detectCountry) return

    const getCountryCode = detectCountry
      ? getGeoCountryCode
      : (): Promise<string> => Promise.resolve(value ? value.code : '')

    getCountryCode()
      .then((country) => getCountryByCode(country))
      .then((countryValue) => {
        if (countryValue && onChange) {
          onChange(countryValue)
        }
      })
  }

  requestCountries = (query: string): Promise<any> => {
    const normQuery = query.toLowerCase().trim()

    return getCountries().then((countries) => {
      if (!query) return countries

      const result = []
      countries.forEach((country) => {
        const countryStr = this.renderCountryToString(country).toLowerCase()
        if (
          country.code.toLowerCase().startsWith(normQuery) ||
          country.name.toLowerCase().startsWith(normQuery) ||
          country.callingCode.startsWith(normQuery)
        ) {
          result.unshift(country)
        } else if (
          country.code.toLowerCase().indexOf(normQuery) >= 0 ||
          country.name.toLowerCase().indexOf(normQuery) >= 0 ||
          country.callingCode.indexOf(normQuery) >= 0 ||
          countryStr.indexOf(normQuery) >= 0
        ) {
          result.push(country)
        }
      })

      return result
    })
  }

  renderCountryToString = (country: Value) =>
    `${country.name} (${country.callingCode})`

  renderPrefixFlag = () => {
    const {
      input: { value },
    } = this.props
    const flag = value.flagCountryCode || value.code
    return <FlagLabel country={flag} />
  }

  renderEmpty = (query: string) => {
    if (!query) return null
    return (
      <EmptySearchResults
        query={query}
        emptyResultsMessage={
          <FormattedMessage
            message={messages.NO_COUNTRY_FOUND}
            values={{ query: <b>{query}</b> }}
          />
        }
      />
    )
  }

  renderOption = (country: Value) => {
    const flag = country.flagCountryCode || country.code
    return (
      <FlagLabel country={flag}>
        {country.name}&nbsp;({country.callingCode})
      </FlagLabel>
    )
  }

  render() {
    const hasPrefix = !!this.props.input?.value
    return (
      <SelectField
        {...this.props}
        renderPrefix={hasPrefix ? this.renderPrefixFlag : null}
        emptyValue={null}
        optionSize={36}
        searchableOptions={{
          ...this.props.searchableOptions,
          requestOptions: this.requestCountries,
          renderEmpty: this.renderEmpty,
        }}
        renderValue={this.renderCountryToString}
        renderOption={this.renderOption}
      />
    )
  }
}

export default CountryField
